/** Append `/// urgent` (or `/// super urgent`) as its own segment if not already present. */
export function addUrgentToTitle(full: string, superUrgent = false): string {
  const word = superUrgent ? 'super urgent' : 'urgent'
  const segments = full.split('///')
  if (segments.some((s) => s.trim().toLowerCase() === word)) return full
  return `${full.trimEnd()} /// ${word}`
}

/** Sort rank by priority: low 0, medium 1, urgent 2, super urgent 3. */
export function priorityRank(t: { effort: string; superUrgent?: boolean | null }): number {
  if (t.superUrgent && t.effort === 'high') return 3
  return t.effort === 'high' ? 2 : t.effort === 'medium' ? 1 : 0
}

/** Remove any `/// urgent` segment (exact match, case-insensitive). */
export function removeUrgentFromTitle(full: string): string {
  const segments = full.split('///')
  const filtered = segments.filter((s) => {
    const w = s.trim().toLowerCase()
    return w !== 'urgent' && !SUPER_URGENT_WORDS.has(w)
  })
  if (filtered.length === segments.length) return full
  return filtered.map((s, i) => (i === filtered.length - 1 ? s.trimEnd() : s)).join('///')
}

/** Extract #hashtags from a task title. Returns lowercase tag names without the # prefix. */
export function extractTags(title: string): string[] {
  const matches = title.match(/#([\wÀ-ɏ]+)/g)
  if (!matches) return []
  return [...new Set(matches.map((m) => m.slice(1).toLowerCase()))]
}

/** Strip #hashtags from a title, collapsing extra whitespace */
export function stripTags(title: string): string {
  return title
    .replace(/#[\wÀ-ɏ]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const SUPER_URGENT_WORDS = new Set(['super urgent', '!!'])

// 'urgent' maps to 'high'; legacy low/medium/high still accepted
const EFFORT_WORDS = new Map<string, 'low' | 'medium' | 'high'>([
  ['super urgent', 'high'],
  ['!!', 'high'],
  ['urgent', 'high'],
  ['high', 'high'],
  ['medium', 'medium'],
  ['low', 'low'],
])

/**
 * Parse a raw task input. `///` segments whose non-tag text matches an effort/priority
 * keyword (urgent, high, medium, low) become the effort. The first non-effort `///` segment
 * becomes the description. Tags from every segment are consolidated into the title.
 *
 * e.g. "Buy coffee /// organic only please /// urgent #organic" →
 *   storedTitle: "Buy coffee #organic /// Organic only please", effort: "high"
 */
export function parseTaskInput(raw: string): {
  storedTitle: string
  effort: 'low' | 'medium' | 'high' | null
  superUrgent: boolean
} {
  const segments = raw.split('///')
  const titleRaw = (segments[0] ?? '').trim()
  const rest = segments
    .slice(1)
    .map((s) => s.trim())
    .filter(Boolean)

  let effort: 'low' | 'medium' | 'high' | null = null
  let superUrgent = false
  const descParts: string[] = []
  const extraTags: string[] = []

  for (const seg of rest) {
    const segTags = extractTags(seg)
    const withoutTags = stripTags(seg).trim()
    const lower = withoutTags.toLowerCase()
    const mapped = EFFORT_WORDS.get(lower)
    if (mapped && !effort) {
      effort = mapped
      superUrgent = SUPER_URGENT_WORDS.has(lower)
      // Collect tags that appeared alongside the priority keyword
      extraTags.push(...segTags)
    } else {
      // Keep description clean (no tags inline), but collect tags. Don't
      // capitalize — preserve the description exactly as the user typed it.
      if (withoutTags) descParts.push(withoutTags)
      extraTags.push(...segTags)
    }
  }

  // Merge tags from title segment + all other segments, deduplicated
  const titleTags = extractTags(titleRaw)
  const cleanTitleText = stripTags(titleRaw).trim()
  const allTags = [...new Set([...titleTags, ...extraTags])]
  const tagStr = allTags.map((t) => `#${t}`).join(' ')

  const capTitle = cleanTitleText.charAt(0).toUpperCase() + cleanTitleText.slice(1)
  const titleWithTags = tagStr ? `${capTitle} ${tagStr}` : capTitle

  const storedTitle = descParts.length
    ? `${titleWithTags} /// ${descParts.join(' /// ')}`
    : titleWithTags

  return { storedTitle, effort, superUrgent }
}

/**
 * Parse a raw task input into separate structured fields (stored in dedicated
 * backend columns rather than packed into the title).
 *
 * Format: `title /// description /// urgent #tag1 #tag2`
 * - First segment → title (tags stripped).
 * - `///` segments matching an effort keyword (urgent/high/medium/low) → effort.
 * - Other `///` segments → description (joined, tags stripped).
 * - `#hashtags` from every segment → tags (deduped, lowercase).
 */
export function parseTaskFields(raw: string): {
  title: string
  description: string | null
  tags: string[]
  effort: 'low' | 'medium' | 'high' | null
  superUrgent: boolean
} {
  const segments = raw.split('///')
  const titleRaw = (segments[0] ?? '').trim()
  const rest = segments
    .slice(1)
    .map((s) => s.trim())
    .filter(Boolean)

  let effort: 'low' | 'medium' | 'high' | null = null
  let superUrgent = false
  const descParts: string[] = []
  const tags: string[] = [...extractTags(titleRaw)]

  for (const seg of rest) {
    tags.push(...extractTags(seg))
    const withoutTags = stripTags(seg).trim()
    const mapped = EFFORT_WORDS.get(withoutTags.toLowerCase())
    if (mapped && !effort) {
      effort = mapped
      superUrgent = SUPER_URGENT_WORDS.has(withoutTags.toLowerCase())
    } else if (withoutTags) {
      // Preserve the description exactly as typed — don't force-capitalize.
      descParts.push(withoutTags)
    }
  }

  const cleanTitle = stripTags(titleRaw).trim()
  const title = cleanTitle.charAt(0).toUpperCase() + cleanTitle.slice(1)

  return {
    title,
    description: descParts.length ? descParts.join('\n') : null,
    tags: [...new Set(tags)],
    effort,
    superUrgent,
  }
}

// ── Task display helpers ──────────────────────────────────────────────────────
// Prefer the structured fields (new tasks store title/description/tags separately);
// fall back to parsing the title for legacy tasks that packed everything in.

type TaskLike = { title: string; description?: string | null; tags?: string[] | null }

export function taskTitle(t: TaskLike): string {
  return stripTags(parseTaskTitle(t.title).title)
}

export function taskDescription(t: TaskLike): string | null {
  return t.description ?? parseTaskTitle(t.title).description
}

export function taskTags(t: TaskLike): string[] {
  return t.tags && t.tags.length ? t.tags : extractTags(t.title)
}

/** Split a title on `///` into a display title and optional description.
 *  `///` segments that are just an effort keyword are silently ignored. */
export function parseTaskTitle(raw: string): { title: string; description: string | null } {
  const segments = raw.split('///')
  const title = (segments[0] ?? '').trim()
  const descParts = segments
    .slice(1)
    .map((s) => s.trim())
    .filter((s) => s && !EFFORT_WORDS.has(s.toLowerCase()))
  const description = descParts.length > 0 ? descParts.join(' /// ') : null
  return { title, description }
}
